import { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import { Eye, Plus, Edit, ChevronLeft, ChevronRight, ArrowLeft, Check, X, AlertTriangle } from 'lucide-react';
import Header from '../../components/Header';
import UserFriendlyDateInput from '../../components/UserFriendlyDateInput';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
const TODAY_DATE = new Date().toISOString().split('T')[0];

// ─── Retire Modal ────────────────────────────────────────────────────────────
function RetireModal({ animal, onClose, onConfirm }) {
  const toast = useToast();
  const [reason, setReason] = useState('');        // 'died' | 'sold'
  const [deathDate, setDeathDate] = useState(new Date().toISOString().split('T')[0]);
  const [soldTo, setSoldTo] = useState('');
  const [soldAmount, setSoldAmount] = useState('');
  const [soldDate, setSoldDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!reason) { toast.warning('Please select died or sold'); return; }
    if (reason === 'died' && !deathDate) { toast.warning('Please enter death date'); return; }
    if (reason === 'sold') {
      if (!soldTo.trim()) { toast.warning('Please enter who the animal was sold to'); return; }
      if (!soldAmount || isNaN(parseFloat(soldAmount))) { toast.warning('Please enter a valid sale amount'); return; }
    }
    try {
      setSubmitting(true);
      const payload = { reason };
      if (reason === 'died') payload.death_date = deathDate;
      if (reason === 'sold') {
        payload.sold_to = soldTo;
        payload.sold_amount = parseFloat(soldAmount);
        payload.sold_date = soldDate;
      }
      await onConfirm(animal.tag_number, payload);
      onClose();
    } catch (e) {
      toast.error('Error: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '460px', margin: '1rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={20} color="#f59e0b" />
            Animal #{animal.tag_number} — Retire
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <div className="form-group">
            <label className="form-label">Reason *</label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                className={`btn ${reason === 'died' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setReason('died')}
              >
                Died
              </button>
              <button
                type="button"
                className={`btn ${reason === 'sold' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setReason('sold')}
              >
                Sold
              </button>
            </div>
          </div>

          {reason === 'died' && (
            <div className="form-group">
              <label className="form-label">Date of Death *</label>
              <input
                type="date" max={TODAY_DATE}
                className="form-input"
                value={deathDate}
                onChange={e => setDeathDate(e.target.value)}
              />
            </div>
          )}

          {reason === 'sold' && (
            <>
              <div className="form-group">
                <label className="form-label">Sold To *</label>
                <input
                  type="text"
                  className="form-input"
                  value={soldTo}
                  onChange={e => setSoldTo(e.target.value)}
                  placeholder="Name of buyer"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={soldAmount}
                  onChange={e => setSoldAmount(e.target.value)}
                  placeholder="Sale amount"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Sale</label>
                <input
                  type="date" max={TODAY_DATE}
                  className="form-input"
                  value={soldDate}
                  onChange={e => setSoldDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="form-actions" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={submitting || !reason}>
            {submitting ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Main AnimalsPage ─────────────────────────────────────────────────────────
function AnimalsPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [khatas, setKhatas] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [animalVendors, setAnimalVendors] = useState([]);
  const [weeklyMilkData, setWeeklyMilkData] = useState([]);
  const [milkSummary, setMilkSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [retireTarget, setRetireTarget] = useState(null);  // animal to retire

  // Daily milk form
  const [showMilkForm, setShowMilkForm] = useState(false);
  const [editingMilkRecord, setEditingMilkRecord] = useState(null);
  const [milkFormData, setMilkFormData] = useState({ record_date: '', milk_morning: '', milk_night: '' });

  const emptyForm = {
    acquisition_type: 'born',   // 'born' | 'bought'
    tag_number: '',
    tabela_id: '',
    khata_id: '',
    type: '',
    // born-specific
    gender: '',
    born_date: '',
    mother_tag_no: '',
    // shared optional
    pregnant_type: '',
    last_conceived: '',
    carving_date: '',
    delivery_date: '',
    available: true,
    // bought-specific
    vendor_id: '',
    bought_from: '',
    price: '',
    lactation: '',
    breed: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [filters, setFilters] = useState({
    tag_number: '',
    tabela: '',
    khata_no: '',
    type: '',
    status: '',
  });

  const [milkCurrentPage, setMilkCurrentPage] = useState(1);
  const [milkPagination, setMilkPagination] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [animalsRes, khatasRes, tabelasRes, vendorsRes] = await Promise.all([
        apiService.getAnimals(),
        apiService.getKhata(),
        apiService.getTabela(),
        apiService.getVendors({ vendor_type: 'animal' }),
      ]);
      setData(animalsRes.data || []);
      setKhatas(khatasRes.data || []);
      setTabelas(tabelasRes.data || []);
      setAnimalVendors(vendorsRes.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyMilk = async (tagNumber, page = 1) => {
    try {
      setLoading(true);
      const res = await apiService.getAnimalWeeklyMilk(tagNumber, { weeks_back: 104, page, per_page: 50 });
      setWeeklyMilkData(res.data || []);
      setMilkSummary(res.summary || {});
      setMilkPagination(res.pagination || {});
    } catch (err) {
      setError('Failed to load milk data');
      setWeeklyMilkData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

  const getKhataNo = (khataId) => {
    const k = khatas.find(k => k.khata_id === khataId);
    return k ? k.khata_no : '—';
  };

  const getTabelaName = (khataId) => {
    const k = khatas.find(k => k.khata_id === khataId);
    if (!k) return '—';
    const t = tabelas.find(t => t.tabela_id === k.tabela_id);
    return t ? t.name : '—';
  };

  const getTabelaId = (khataId) => {
    const k = khatas.find(k => k.khata_id === khataId);
    return k ? k.tabela_id : '';
  };

  const getFilteredKhatas = () => {
    if (!formData.tabela_id) return [];
    return khatas.filter(k => k.tabela_id === parseInt(formData.tabela_id));
  };

  const filteredData = data.filter(item => {
    const khataNo = getKhataNo(item.khata_id).toString();
    const tabelaName = getTabelaName(item.khata_id).toLowerCase();
    return (
      (filters.tag_number === '' || item.tag_number.toLowerCase().includes(filters.tag_number.toLowerCase())) &&
      (filters.tabela === '' || tabelaName.includes(filters.tabela.toLowerCase())) &&
      (filters.khata_no === '' || khataNo.includes(filters.khata_no)) &&
      (filters.type === '' || (item.type || '').toLowerCase().includes(filters.type.toLowerCase())) &&
      (filters.status === '' || (item.status || 'active') === filters.status)
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => { setCurrentPage(1); }, [filters]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const fmt = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return isNaN(d) ? '' : d.toISOString().split('T')[0];
    };
    setFormData({
      acquisition_type: item.acquisition_type || 'born',
      tag_number: item.tag_number,
      tabela_id: getTabelaId(item.khata_id).toString(),
      khata_id: item.khata_id?.toString() || '',
      type: item.type || '',
      gender: item.gender || '',
      born_date: fmt(item.born_date),
      mother_tag_no: item.mother_tag_no || '',
      pregnant_type: item.pregnant_type || '',
      last_conceived: fmt(item.last_conceived),
      carving_date: fmt(item.carving_date),
      delivery_date: fmt(item.delivery_date),
      available: item.available !== false,
      vendor_id: '', // editing path uses /animals — vendor stays on the AnimalPurchase row
      bought_from: item.bought_from || '',
      price: '',
      lactation: item.lactation || '',
      breed: item.breed || '',
    });
    setCurrentView('form');
  };

  const handleView = async (item) => {
    setViewingItem(item);
    setCurrentView('detail');
    setMilkCurrentPage(1);
    await loadWeeklyMilk(item.tag_number, 1);
  };

  const handleRetire = (item) => setRetireTarget(item);

  const confirmRetire = async (tagNumber, payload) => {
    await apiService.retireAnimal(tagNumber, payload);
    await loadData();
  };

  const handleSubmit = async () => {
    if (!formData.tag_number || !formData.khata_id || !formData.type) {
      toast.warning('Tag number, khata, and type are required');
      return;
    }
    if (formData.acquisition_type === 'born' && !formData.pregnant_type) {
      toast.warning('Pregnant type is required');
      return;
    }

    const fmt = (v) => {
      if (!v || v === '') return null;
      const d = new Date(v);
      return isNaN(d) ? null : d.toISOString().split('T')[0];
    };

    // ── NEW animal that was BOUGHT: route through /animal-purchases so the
    // accounting record + vendor link are created in one shot.
    if (!editingItem && formData.acquisition_type === 'bought') {
      if (!formData.vendor_id) { toast.warning('Please select an animal vendor'); return; }
      if (!formData.price || isNaN(parseFloat(formData.price))) { toast.warning('Valid price is required for bought animals'); return; }

      const payload = {
        date: fmt(formData.born_date) || new Date().toISOString().split('T')[0],
        vendor_id: parseInt(formData.vendor_id),
        bought_from: formData.bought_from || null,
        type: formData.type,
        breed: formData.breed || null,
        lactation: formData.lactation ? parseInt(formData.lactation) : null,
        price: parseFloat(formData.price),
        tag_number: formData.tag_number.toString().toUpperCase(),
        khata_id: parseInt(formData.khata_id),
        notes: null,
      };
      try {
        setLoading(true);
        await apiService.createAnimalPurchase(payload);
        await loadData();
        setCurrentView('list');
        setFormData(emptyForm);
      } catch (err) {
        toast.error('Error saving animal: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Existing path: born animal, or editing any animal
    const submitData = {
      tag_number: formData.tag_number.toString().toUpperCase(),
      khata_id: parseInt(formData.khata_id),
      type: formData.type,
      available: Boolean(formData.available),
      pregnant_type: formData.pregnant_type,
      last_conceived: fmt(formData.last_conceived),
      carving_date: fmt(formData.carving_date),
      delivery_date: fmt(formData.delivery_date),
      acquisition_type: formData.acquisition_type,
      gender: formData.gender || null,
      born_date: fmt(formData.born_date),
      mother_tag_no: formData.mother_tag_no || null,
      bought_from: formData.bought_from || null,
      lactation: formData.lactation ? parseInt(formData.lactation) : null,
      breed: formData.breed || null,
    };

    try {
      setLoading(true);
      if (editingItem) {
        await apiService.updateAnimal(editingItem.tag_number, submitData);
      } else {
        await apiService.createAnimal(submitData);
      }
      await loadData();
      setCurrentView('list');
      setFormData(emptyForm);
      setEditingItem(null);
    } catch (err) {
      toast.error('Error saving animal: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    if (name === 'tag_number') newValue = String(newValue).toUpperCase();
    if (name === 'tabela_id') {
      setFormData({ ...formData, tabela_id: newValue, khata_id: '' });
    } else {
      setFormData({ ...formData, [name]: newValue });
    }
  };

  const handleMilkInputChange = (e) => {
    setMilkFormData({ ...milkFormData, [e.target.name]: e.target.value });
  };

  const handleAddDailyMilk = () => {
    setEditingMilkRecord(null);
    setMilkFormData({ record_date: new Date().toISOString().split('T')[0], milk_morning: '', milk_night: '' });
    setShowMilkForm(true);
  };

  const handleEditDailyMilk = (record) => {
    setEditingMilkRecord(record);
    const norm = (v) => {
      if (!v) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return String(v);
      const d = new Date(v);
      return isNaN(d) ? '' : d.toISOString().split('T')[0];
    };
    setMilkFormData({
      record_date: norm(record.week_start),
      milk_morning: record.total_morning.toString(),
      milk_night: record.total_night.toString(),
    });
    setShowMilkForm(true);
  };

  const handleDeleteDailyMilk = async (record) => {
    if (window.confirm(`Delete milk record for ${formatDate(record.week_start)}?`)) {
      try {
        await apiService.deleteAnimalWeeklyMilk(viewingItem.tag_number, record.record_id);
        await loadWeeklyMilk(viewingItem.tag_number, milkCurrentPage);
      } catch (err) {
        toast.error('Error deleting record: ' + err.message);
      }
    }
  };

  const handleMilkFormSubmit = async () => {
    if (!milkFormData.record_date || milkFormData.milk_morning === '' || milkFormData.milk_night === '') {
      toast.warning('Please fill all fields');
      return;
    }
    const norm = (v) => {
      if (!v) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return String(v);
      const d = new Date(v);
      return isNaN(d) ? v : d.toISOString().split('T')[0];
    };
    const submitData = {
      record_date: norm(milkFormData.record_date),
      record_type: 'daily',
      milk_morning: parseFloat(milkFormData.milk_morning),
      milk_night: parseFloat(milkFormData.milk_night),
    };
    if (isNaN(submitData.milk_morning) || isNaN(submitData.milk_night)) {
      toast.warning('Milk quantities must be valid numbers');
      return;
    }
    try {
      setLoading(true);
      if (editingMilkRecord) {
        await apiService.updateAnimalWeeklyMilk(viewingItem.tag_number, editingMilkRecord.record_id, submitData);
      } else {
        await apiService.createAnimalWeeklyMilk(viewingItem.tag_number, submitData);
      }
      await loadWeeklyMilk(viewingItem.tag_number, milkCurrentPage);
      setShowMilkForm(false);
      setMilkFormData({ record_date: '', milk_morning: '', milk_night: '' });
      setEditingMilkRecord(null);
    } catch (err) {
      toast.error('Error saving record: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingItem(null);
    setViewingItem(null);
    setShowMilkForm(false);
    setFormData(emptyForm);
  };

  // ─── Status badge helper ─────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const cfg = {
      active: { bg: '#dcfce7', color: '#166534', label: 'Active' },
      died:   { bg: '#fee2e2', color: '#991b1b', label: 'Died' },
      sold:   { bg: '#fef3c7', color: '#92400e', label: 'Sold' },
    };
    const s = cfg[status] || cfg.active;
    return (
      <span style={{ padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 600, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  // ─── Display tag (hide RETIRED- prefix) ─────────────────────────────────────
  const displayTag = (animal) => {
    if ((animal.status === 'died' || animal.status === 'sold') && animal.tag_number.startsWith('RETIRED-')) {
      // Extract original tag: "RETIRED-{id}-{original}"
      const parts = animal.tag_number.split('-');
      return parts.slice(2).join('-') + ' (retired)';
    }
    return animal.tag_number;
  };

  // ─── Loading / Error ──────────────────────────────────────────────────────────
  const wrapLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  if (loading && data.length === 0) return wrapLayout(
    <div className="loading-container"><div className="spinner"></div><p>Loading animals...</p></div>
  );

  if (error && data.length === 0) return wrapLayout(
    <div className="error-message">Error: {error} <button className="btn btn-primary" onClick={loadData}>Retry</button></div>
  );

  // ─── Detail View ──────────────────────────────────────────────────────────────
  if (currentView === 'detail') {
    const animal = viewingItem;
    return wrapLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel}>
              <ArrowLeft size={18} /> Back to Animals List
            </button>
            <div style={{ marginTop: '1rem' }}>
              <h1 className="page-title">Animal Detail — Tag #{displayTag(animal)}</h1>
            </div>
          </div>
          {animal.status === 'active' && (
            <button className="btn btn-primary" onClick={() => handleEdit(animal)}>
              <Edit size={18} /> Edit Animal
            </button>
          )}
        </div>

        <div className="form-row">
          <div className="card" style={{ flex: 1 }}>
            <div className="card-header"><h3 className="card-title">Animal Information</h3></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {[
                ['Tag No', displayTag(animal)],
                ['Status', <StatusBadge status={animal.status || 'active'} />],
                ['Acquisition', animal.acquisition_type === 'bought' ? 'Bought' : 'Born'],
                ['Tabela', getTabelaName(animal.khata_id)],
                ['Khata No', getKhataNo(animal.khata_id)],
                ['Type', animal.type],
                ['Breed', animal.breed || 'N/A'],
                ['Gender', animal.gender || 'N/A'],
                ['Born / Acquired Date', formatDate(animal.born_date)],
                ['Mother Tag No', animal.mother_tag_no || 'N/A'],
                ['Bought From', animal.bought_from || 'N/A'],
                ['Lactation', animal.lactation || 'N/A'],
                ['Pregnant Type', animal.pregnant_type || 'N/A'],
                ['Last Conceived', formatDate(animal.last_conceived)],
                ['Carving Date', formatDate(animal.carving_date)],
                ['Delivery Date', formatDate(animal.delivery_date)],
                animal.status === 'died' ? ['Date of Death', formatDate(animal.death_date)] : null,
                animal.status === 'sold' ? ['Sold To', animal.sold_to] : null,
                animal.status === 'sold' ? ['Sale Amount', animal.sold_amount ? `₹${animal.sold_amount}` : 'N/A'] : null,
                animal.status === 'sold' ? ['Date of Sale', formatDate(animal.sold_date)] : null,
              ].filter(Boolean).map(([label, val]) => (
                <div className="form-group" key={label}>
                  <label className="form-label">{label}</label>
                  <div style={{ fontSize: '1rem' }}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ flex: 1 }}>
            <div className="card-header"><h3 className="card-title">Milk Production Summary</h3></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
              {[
                ['Total Milk Produced', `${milkSummary.total_milk?.toFixed(2) || 0} L`, '#eff6ff', '#1d4ed8', '#1e40af'],
                ['Average Daily', `${milkSummary.avg_daily_milk?.toFixed(2) || 0} L/day`, '#f0fdf4', '#15803d', '#166534'],
                ['Total Records', `${milkSummary.total_records || 0} days`, '#fef3c7', '#b45309', '#92400e'],
              ].map(([label, val, bg, lc, vc]) => (
                <div key={label} style={{ background: bg, padding: '1.5rem', borderRadius: '8px', textAlign: 'center' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: lc }}>{label}</label>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: vc }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Milk Records */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="card-title">Daily Milk Records</h3>
            <button className="btn btn-primary btn-sm" onClick={handleAddDailyMilk}>
              <Plus size={16} /> Add Daily Record
            </button>
          </div>
          <div className="table-container" style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Record Date</th>
                  <th>Morning (L)</th>
                  <th>Evening (L)</th>
                  <th>Total (L)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {weeklyMilkData.map((item, i) => (
                  <tr key={item.record_id || i}>
                    <td data-label="Record Date"><strong>{formatDate(item.week_start)}</strong></td>
                    <td data-label="Morning (L)">{item.total_morning.toFixed(2)}</td>
                    <td data-label="Evening (L)">{item.total_night.toFixed(2)}</td>
                    <td data-label="Total (L)"><strong style={{ color: 'var(--primary)' }}>{item.total_week.toFixed(2)}</strong></td>
                    <td data-label="Actions">
                      <div className="actions-cell">
                        <button className="btn-icon" onClick={() => handleEditDailyMilk(item)} title="Edit"><Edit size={18} /></button>
                        <button className="btn-icon" style={{ color: 'var(--error)' }} onClick={() => handleDeleteDailyMilk(item)} title="Delete"><X size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {weeklyMilkData.length === 0 && !loading && (
              <div className="no-data">
                <p>No daily milk records found.</p>
                <button className="btn btn-primary btn-sm" onClick={handleAddDailyMilk} style={{ marginTop: '1rem' }}>
                  <Plus size={16} /> Add First Record
                </button>
              </div>
            )}
          </div>
          {milkPagination.pages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" onClick={() => { setMilkCurrentPage(p => p - 1); loadWeeklyMilk(viewingItem.tag_number, milkCurrentPage - 1); }} disabled={milkCurrentPage === 1}>
                <ChevronLeft size={16} />
              </button>
              {[...Array(milkPagination.pages)].map((_, i) => (
                <button key={i + 1} className={`pagination-btn ${milkCurrentPage === i + 1 ? 'active' : ''}`} onClick={() => { setMilkCurrentPage(i + 1); loadWeeklyMilk(viewingItem.tag_number, i + 1); }}>{i + 1}</button>
              ))}
              <button className="pagination-btn" onClick={() => { setMilkCurrentPage(p => p + 1); loadWeeklyMilk(viewingItem.tag_number, milkCurrentPage + 1); }} disabled={milkCurrentPage === milkPagination.pages}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Daily Milk Form Modal */}
        {showMilkForm && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '500px', margin: '1rem', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title">{editingMilkRecord ? 'Edit Daily Milk Record' : 'Add Daily Milk Record'}</h3>
                <button className="btn-icon" onClick={() => setShowMilkForm(false)}><X size={20} /></button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div className="form-group">
                  <label className="form-label">Record Date</label>
                  <UserFriendlyDateInput name="record_date" className="form-input" value={milkFormData.record_date} onChange={handleMilkInputChange} disabled={loading} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Morning (L)</label>
                    <input type="number" step="0.1" min="0" name="milk_morning" className="form-input" value={milkFormData.milk_morning} onChange={handleMilkInputChange} placeholder="Morning" disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Evening (L)</label>
                    <input type="number" step="0.1" min="0" name="milk_night" className="form-input" value={milkFormData.milk_night} onChange={handleMilkInputChange} placeholder="Evening" disabled={loading} />
                  </div>
                </div>
                {milkFormData.milk_morning && milkFormData.milk_night && (
                  <div className="alert alert-info">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Day:</span>
                      <strong>{(parseFloat(milkFormData.milk_morning || 0) + parseFloat(milkFormData.milk_night || 0)).toFixed(2)} L</strong>
                    </div>
                  </div>
                )}
              </div>
              <div className="form-actions" style={{ padding: '1.5rem', borderTop: '1px solid var(--border)' }}>
                <button className="btn btn-secondary" onClick={() => setShowMilkForm(false)} disabled={loading}>Cancel</button>
                <button className="btn btn-primary" onClick={handleMilkFormSubmit} disabled={loading}>
                  {loading ? 'Saving...' : (editingMilkRecord ? 'Update' : 'Add Record')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Form View ────────────────────────────────────────────────────────────────
  if (currentView === 'form') {
    const isBorn = formData.acquisition_type === 'born';
    return wrapLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel}>
              <ArrowLeft size={18} /> Back to Animals List
            </button>
            <div style={{ marginTop: '1rem' }}>
              <h1 className="page-title">{editingItem ? 'Edit Animal' : 'Add New Animal'}</h1>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-error"><p>{error}</p></div>}

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Acquisition type toggle — top of form */}
          {!editingItem && (
            <div className="form-group">
              <label className="form-label">How was this animal acquired? *</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="button"
                  className={`btn ${isBorn ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFormData({ ...formData, acquisition_type: 'born' })}
                >
                  Born
                </button>
                <button
                  type="button"
                  className={`btn ${!isBorn ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFormData({ ...formData, acquisition_type: 'bought' })}
                >
                  Bought
                </button>
              </div>
            </div>
          )}

          {/* Tag number — always shown */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Animal Tag Number *</label>
              <input type="text" name="tag_number" className="form-input" value={formData.tag_number} onChange={handleInputChange} placeholder="e.g. A101" disabled={loading || !!editingItem} />
            </div>
            <div className="form-group">
              <label className="form-label">Type (Cow / Buffalo) *</label>
              <select name="type" className="form-select" value={formData.type} onChange={handleInputChange} disabled={loading}>
                <option value="">Select Type</option>
                <option value="Cow">Cow</option>
                <option value="Buffalo">Buffalo</option>
              </select>
            </div>
          </div>

          {/* Tabela + Khata — always shown */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Tabela *</label>
              <select name="tabela_id" className="form-select" value={formData.tabela_id} onChange={handleInputChange} disabled={loading}>
                <option value="">Select Tabela</option>
                {sortedByName(tabelas).map(t => <option key={t.tabela_id} value={t.tabela_id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Khata *</label>
              <select name="khata_id" className="form-select" value={formData.khata_id} onChange={handleInputChange} disabled={loading || !formData.tabela_id}>
                <option value="">Select Khata</option>
                {sortedByName(getFilteredKhatas(), k => k.khata_no).map(k => <option key={k.khata_id} value={k.khata_id}>{k.khata_no}</option>)}
              </select>
            </div>
          </div>

          {/* ── BORN fields ── */}
          {isBorn && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-select" value={formData.gender} onChange={handleInputChange} disabled={loading}>
                    <option value="">Select Gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <UserFriendlyDateInput name="born_date" className="form-input" value={formData.born_date} onChange={handleInputChange} disabled={loading} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Mother Tag No</label>
                <input type="text" name="mother_tag_no" className="form-input" value={formData.mother_tag_no} onChange={handleInputChange} placeholder="Mother's tag number" disabled={loading} />
              </div>
            </>
          )}

          {/* ── BOUGHT fields ── */}
          {!isBorn && (
            <>
              {!editingItem && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Vendor *</label>
                    <select
                      name="vendor_id"
                      className="form-select"
                      value={formData.vendor_id}
                      onChange={handleInputChange}
                      disabled={loading}
                    >
                      <option value="">Select Animal Vendor</option>
                      {sortedByName(animalVendors).map(v => (
                        <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                      ))}
                    </select>
                    {animalVendors.length === 0 && (
                      <small style={{ color: 'var(--error-color)' }}>
                        No animal vendors found. Add one in Vendors (Portfolio) with type = Animal.
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Purchase *</label>
                    <UserFriendlyDateInput name="born_date" className="form-input" value={formData.born_date} onChange={handleInputChange} disabled={loading} />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Bought From (farmer name, optional)</label>
                  <input type="text" name="bought_from" className="form-input" value={formData.bought_from} onChange={handleInputChange} placeholder="Original farmer's name (if any)" disabled={loading} />
                </div>
                {!editingItem && (
                  <div className="form-group">
                    <label className="form-label">Purchase Price (₹) *</label>
                    <input type="number" name="price" className="form-input" value={formData.price} onChange={handleInputChange} placeholder="Purchase price" min="0" step="0.01" disabled={loading} />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Lactation No.</label>
                  <input type="number" name="lactation" className="form-input" value={formData.lactation} onChange={handleInputChange} placeholder="e.g. 2" min="0" disabled={loading} />
                </div>
                <div className="form-group">
                  <label className="form-label">Breed</label>
                  <input type="text" name="breed" className="form-input" value={formData.breed} onChange={handleInputChange} placeholder="e.g. HF, Murrah" disabled={loading} />
                </div>
              </div>

              {editingItem && (
                <div className="form-group">
                  <label className="form-label">Date of Purchase</label>
                  <UserFriendlyDateInput name="born_date" className="form-input" value={formData.born_date} onChange={handleInputChange} disabled={loading} />
                </div>
              )}
            </>
          )}

          {/* ── Shared optional fields ── */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Pregnant Type *</label>
              <select name="pregnant_type" className="form-select" value={formData.pregnant_type} onChange={handleInputChange} disabled={loading}>
                <option value="">Select</option>
                <option value="Pregnant">Pregnant</option>
                <option value="Non-Pregnant">Non-Pregnant</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Last Conceived</label>
              <UserFriendlyDateInput name="last_conceived" className="form-input" value={formData.last_conceived} onChange={handleInputChange} disabled={loading} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Carving Date</label>
              <UserFriendlyDateInput name="carving_date" className="form-input" value={formData.carving_date} onChange={handleInputChange} disabled={loading} />
            </div>
            {formData.pregnant_type === 'Pregnant' && (
              <div className="form-group">
                <label className="form-label">Expected Delivery</label>
                <UserFriendlyDateInput name="delivery_date" className="form-input" value={formData.delivery_date} onChange={handleInputChange} disabled={loading} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              <input type="checkbox" name="available" checked={formData.available} onChange={handleInputChange} disabled={loading} style={{ marginRight: '0.5rem' }} />
              Available
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={16} />
              {loading ? 'Saving...' : (editingItem ? 'Update Animal' : 'Add Animal')}
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────────────
  return wrapLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">Animals Management</h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Animal
        </button>
      </div>

      {/* Retire Modal */}
      {retireTarget && (
        <RetireModal
          animal={retireTarget}
          onClose={() => setRetireTarget(null)}
          onConfirm={confirmRetire}
        />
      )}

      <div className="filter-bar">
        {[
          { label: 'Tag #', name: 'tag_number', placeholder: 'Search Tag', width: '120px' },
          { label: 'Tabela', name: 'tabela', placeholder: 'Search Tabela' },
          { label: 'Khata No', name: 'khata_no', placeholder: 'Khata No', width: '100px' },
          { label: 'Type', name: 'type', placeholder: 'Cow/Buffalo', width: '100px' },
        ].map(f => (
          <div className="filter-group" key={f.name}>
            <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>{f.label}</label>
            <input type="text" name={f.name} className="form-input" value={filters[f.name]} onChange={e => setFilters({ ...filters, [e.target.name]: e.target.value })} placeholder={f.placeholder} style={f.width ? { width: f.width } : {}} />
          </div>
        ))}
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Status</label>
          <select name="status" className="form-select" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} style={{ width: '110px' }}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="died">Died</option>
            <option value="sold">Sold</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={() => setFilters({ tag_number: '', tabela: '', khata_no: '', type: '', status: '' })} style={{ height: '42px', alignSelf: 'flex-end' }}>Clear</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tag #</th>
              <th>Status</th>
              <th>Type</th>
              <th>Acquired</th>
              <th>Tabela</th>
              <th>Khata</th>
              <th>Pregnant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(item => (
              <tr key={item.animal_id} style={{ opacity: item.status !== 'active' ? 0.75 : 1 }}>
                <td data-label="Tag #" style={{ fontWeight: 600 }}>{displayTag(item)}</td>
                <td data-label="Status"><StatusBadge status={item.status || 'active'} /></td>
                <td data-label="Type">{item.type}</td>
                <td data-label="Acquired"><span style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>{item.acquisition_type === 'bought' ? 'Bought' : 'Born'}</span></td>
                <td data-label="Tabela">{getTabelaName(item.khata_id)}</td>
                <td data-label="Khata">{getKhataNo(item.khata_id)}</td>
                <td data-label="Pregnant">{item.pregnant_type || '—'}</td>
                <td data-label="Actions">
                  <div className="actions-cell">
                    <button className="btn-icon" onClick={() => handleView(item)} title="View"><Eye size={18} /></button>
                    {item.status === 'active' && (
                      <>
                        <button className="btn-icon" onClick={() => handleEdit(item)} title="Edit"><Edit size={18} /></button>
                        <button
                          className="btn-icon"
                          style={{ color: '#f59e0b' }}
                          onClick={() => handleRetire(item)}
                          title="Mark as Died / Sold"
                        >
                          <AlertTriangle size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {currentItems.length === 0 && !loading && (
          <div className="no-data"><p>No animals found matching your filters</p></div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i + 1} className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
          ))}
          <button className="pagination-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {currentItems.length} of {filteredData.length} animals
      </div>
    </>
  );
}

export default AnimalsPage;
